import styled from "styled-components";
import { Column, DatePicker, InputText, InputTextarea, Row, Text } from "@ficatranquilo/contabilidade.com-ui";
import { DomainStatus, escapeHtml } from "../ow";
import { clockDateParts, clockDateTime, maskClockTime, parseClockTime } from "../../helpers/timeClock";

// Peças dos ajustes de ponto (inclusão de batida) usadas pela empresa
// (/c/time-clock/adjustments e o ajuste direto) e pelo colaborador (/e/time-clock).

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "4px"};
  align-items: flex-start;
`;

// Justificativa e motivo em até 2 linhas; o texto inteiro fica no title
export const Clamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// $centralize centraliza o calendário no ancestral posicionado, e o Column da lib é
// relative: sem position, ele centraliza pela linha toda (a largura do modal).
const StaticColumn = styled(Column)`
  position: static;
`;

const DOMAIN_VALUE = { PENDING: "pendente", APPROVED: "aprovado", REJECTED: "reprovado" };

/** Ajuste direto: quem criou o pedido não é o colaborador. */
export const isDirectAdjustment = (adjustment) => adjustment.created_by && adjustment.created_by !== adjustment.user_id;

/** "por Fulano em 23/10/2026", de quem aprovou ou reprovou (o dia no fuso local). */
export function decisionText(adjustment) {
  const [who, when] =
    adjustment.status === "APPROVED"
      ? [adjustment.approvedBy?.name, adjustment.approved_at]
      : adjustment.status === "REJECTED"
        ? [adjustment.rejectedBy?.name, adjustment.rejected_at]
        : [];
  if (!who && !when) return null;
  return [who ? `por ${who}` : null, when ? `em ${clockDateParts(when).date}` : null].filter(Boolean).join(" ");
}

/** Batida solicitada: "22/10/2026" e "às 08:00", no fuso local. */
export function RequestedClockCell({ adjustment }) {
  const { date, time } = clockDateParts(adjustment.requested_clock_time);
  return (
    <div>
      <Text $text={date} $variant="paragraph_small_bold" $color="N900" />
      {time ? <Text $text={`às ${time}`} $variant="caption_medium" $color="N400" /> : null}
    </div>
  );
}

export function AdjustmentReasonCell({ adjustment }) {
  return (
    <Clamp title={adjustment.reason || ""}>
      <Text $text={escapeHtml(adjustment.reason || "-")} $variant="paragraph_small_medium" $color="N900" />
    </Clamp>
  );
}

/** Dia do pedido e, no ajuste direto, "Incluído pelo gestor". */
export function AdjustmentCreatedCell({ adjustment }) {
  return (
    <div>
      <Text $text={clockDateParts(adjustment.created_at).date} $variant="paragraph_small_medium" $color="N900" />
      {isDirectAdjustment(adjustment) ? <Text $text="Incluído pelo gestor" $variant="caption_medium" $color="N400" /> : null}
    </div>
  );
}

/** Status, quem decidiu e quando e, no reprovado, o motivo em até 2 linhas. */
export function AdjustmentStatusCell({ adjustment }) {
  const decision = decisionText(adjustment);
  return (
    <Stack>
      <DomainStatus domain="ajustePonto" value={DOMAIN_VALUE[adjustment.status] || adjustment.status} />
      {decision ? <Text $text={escapeHtml(decision)} $variant="caption_medium" $color="N500" /> : null}
      {adjustment.status === "REJECTED" && adjustment.rejection_reason ? (
        <Clamp title={adjustment.rejection_reason}>
          <Text $text={escapeHtml(`Motivo: ${adjustment.rejection_reason}`)} $variant="caption_medium" $color="N500" />
        </Clamp>
      ) : null}
    </Stack>
  );
}

/** Colunas comuns às duas listas, depois do colaborador e antes das ações. */
export const ADJUSTMENT_COLUMNS = [
  { id: "clock", header: "Batida solicitada", size: 140, cell: ({ row }) => <RequestedClockCell adjustment={row.original} /> },
  { id: "reason", header: "Justificativa", size: 300, cell: ({ row }) => <AdjustmentReasonCell adjustment={row.original} /> },
  { id: "created_at", header: "Pedido em", size: 140, cell: ({ row }) => <AdjustmentCreatedCell adjustment={row.original} /> },
  { id: "status", header: "Status", size: 220, cell: ({ row }) => <AdjustmentStatusCell adjustment={row.original} /> },
];

/**
 * Erros da data, da hora e da justificativa de uma batida, pelos ids dos campos
 * (`ids.date`, `ids.time`, `ids.reason`).
 */
export function validateClockAdjustment(values, ids) {
  const errors = {};
  if (!values.date) errors[ids.date] = "Informe a data da batida";
  if (!values.time) errors[ids.time] = "Informe a hora da batida";
  else if (parseClockTime(values.time) === null) errors[ids.time] = "Use o formato 08:00";
  else {
    const clockedAt = clockDateTime(values.date, values.time);
    if (clockedAt && clockedAt.getTime() > Date.now()) errors[ids.time] = "Horário no futuro";
  }
  if (!values.reason.trim()) errors[ids.reason] = "Informe a justificativa";
  return errors;
}

/**
 * Data e hora da batida lado a lado e a justificativa embaixo, para modal (o
 * calendário abre centralizado). A batida não pode ser anterior a `minDate` (início
 * do contrato) nem estar no futuro; o back confere os dois.
 *
 * Props: ids ({ date, time, reason }), values ({ date, time, reason }), errors,
 * onChange(campo, valor), minDate, reasonPlaceholder.
 */
export function ClockAdjustmentFields({ ids, values, errors, onChange, minDate, reasonPlaceholder }) {
  return (
    <>
      <Row $gap={20}>
        <StaticColumn $columns={6}>
          <DatePicker
            $idForm={ids.date}
            $centralize
            $label="Data da batida"
            $variant="default"
            $isRequired
            $errors={errors[ids.date]}
            $minDate={minDate}
            $maxDate={new Date()}
            $date={values.date}
            $setDate={(date) => onChange("date", date || null)}
          />
        </StaticColumn>
        <Column $columns={6}>
          <InputText
            $idForm={ids.time}
            $label="Hora"
            $variant="default"
            $isRequired
            $errors={errors[ids.time]}
            inputMode="numeric"
            placeholder="08:00"
            value={values.time}
            onChange={(e) => onChange("time", maskClockTime(e.target.value))}
          />
        </Column>
      </Row>
      <InputTextarea
        $idForm={ids.reason}
        $label="Justificativa"
        $variant="default"
        $isRequired
        $errors={errors[ids.reason]}
        placeholder={reasonPlaceholder}
        rows={2}
        value={values.reason}
        onChange={(e) => onChange("reason", e.target.value)}
      />
    </>
  );
}
